import api from './api';
import { downloadFromApi } from './download';

/**
 * Programme Stations (lot 034) : réseau, prix programmés, gérance, registre des
 * pompes, déclaration hebdomadaire, relevé du gérant, reprise Excel.
 *
 * Chemins et formes de réponse : `backend/app/api/v1/station_network.py`.
 * Chaque écriture porte un identifiant choisi ici (clé d'idempotence du
 * serveur) ou une clé `Idempotency-Key` : un renvoi après coupure rend le même
 * résultat. Aucun chiffre n'est calculé ici : tout vient du serveur.
 */

const unwrap = (response) => response?.data?.data ?? response?.data;

const nouvelId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

const sansVides = (corps) =>
  Object.fromEntries(Object.entries(corps || {}).filter(([, valeur]) => valeur !== undefined));

const avecCle = () => ({ headers: { 'Idempotency-Key': nouvelId() } });

const BASE = '/station-network';

export const stationNetworkApi = {
  /** Tableau de bord réseau d'un jour (AAAA-MM-JJ ; la veille par défaut). */
  async summary(date) {
    return unwrap(await api.get(`${BASE}/summary`, { params: date ? { date } : {} }));
  },

  priceChanges: {
    async list(params = {}) {
      return unwrap(await api.get(`${BASE}/price-changes`, { params })) || [];
    },
    /** Programme un prix ; `effectiveAt` en ISO avec fuseau. */
    async schedule({ productId, newPrice, effectiveAt, stationIds, allStations, notes }) {
      return unwrap(
        await api.post(
          `${BASE}/price-changes`,
          sansVides({
            id: nouvelId(),
            product_id: productId,
            new_price: newPrice,
            effective_at: effectiveAt,
            station_ids: allStations ? [] : stationIds || [],
            all_stations: Boolean(allStations),
            notes: notes || undefined,
          }),
        ),
      );
    },
    async cancel(changeId, reason) {
      return unwrap(
        await api.post(`${BASE}/price-changes/${changeId}/cancel`, sansVides({ reason: reason || undefined }), avecCle()),
      );
    },
  },

  management: {
    async get(stationId) {
      return unwrap(await api.get(`${BASE}/stations/${stationId}/management`));
    },
    async update(stationId, data) {
      return unwrap(await api.put(`${BASE}/stations/${stationId}/management`, sansVides(data), avecCle()));
    },
  },

  pumpRegistry: {
    async get(stationId) {
      return unwrap(await api.get(`${BASE}/stations/${stationId}/pump-registry`));
    },
    async createEvent(stationId, data) {
      return unwrap(
        await api.post(`${BASE}/stations/${stationId}/pump-events`, sansVides({ ...data, id: data.id || nouvelId() })),
      );
    },
    async updateEvent(stationId, eventId, data) {
      return unwrap(await api.patch(`${BASE}/stations/${stationId}/pump-events/${eventId}`, sansVides(data), avecCle()));
    },
  },

  declaration: {
    async get(weekStart) {
      return unwrap(await api.get(`${BASE}/weekly-declaration`, { params: { week_start: weekStart } }));
    },
    /** Télécharge le fichier (`pdf` ou `xlsx`). */
    async download(weekStart, format) {
      return downloadFromApi(
        `${BASE}/weekly-declaration/${format}`,
        { week_start: weekStart },
        `declaration-hebdomadaire.${format}`,
      );
    },
  },

  statements: {
    async list(params = {}) {
      return unwrap(await api.get(`${BASE}/manager-statements`, { params })) || [];
    },
    async get(stationId, year, month) {
      return unwrap(await api.get(`${BASE}/stations/${stationId}/manager-statement`, { params: { year, month } }));
    },
    /** Calcul avec un manquant ou un prix de revient saisis, sans rien écrire. */
    async preview(stationId, { year, month, shortages = [], unitCosts = [] }) {
      return unwrap(
        await api.post(`${BASE}/stations/${stationId}/manager-statement/preview`, {
          year,
          month,
          shortages: shortages.map((ligne) => ({ product_id: ligne.productId, liters: ligne.liters })),
          unit_costs: unitCosts.map((ligne) => ({ product_id: ligne.productId, unit_cost: ligne.unitCost })),
        }),
      );
    },
    async freeze(stationId, { year, month, shortages = [], unitCosts = [] }) {
      return unwrap(
        await api.post(`${BASE}/stations/${stationId}/manager-statement/freeze`, {
          id: nouvelId(),
          year,
          month,
          shortages: shortages.map((ligne) => ({ product_id: ligne.productId, liters: ligne.liters })),
          unit_costs: unitCosts.map((ligne) => ({ product_id: ligne.productId, unit_cost: ligne.unitCost })),
        }),
      );
    },
    async void(statementId, reason) {
      return unwrap(await api.post(`${BASE}/manager-statements/${statementId}/void`, { reason }, avecCle()));
    },
    async download(stationId, year, month, format) {
      return downloadFromApi(
        `${BASE}/stations/${stationId}/manager-statement/${format}`,
        { year, month },
        `releve-gerant.${format}`,
      );
    },
  },

  importNetwork: {
    async template() {
      return downloadFromApi(`${BASE}/import/template`, {}, 'reprise-reseau-modele.xlsx');
    },
    /** Envoie le classeur ; `dryRun` vérifie sans rien écrire. */
    async run(file, { dryRun = false } = {}) {
      const formulaire = new FormData();
      formulaire.append('file', file);
      formulaire.append('dry_run', dryRun ? 'true' : 'false');
      return unwrap(
        await api.post(`${BASE}/import`, formulaire, {
          headers: { 'Content-Type': 'multipart/form-data', ...(dryRun ? {} : { 'Idempotency-Key': nouvelId() }) },
          timeout: 120000,
        }),
      );
    },
  },
};

export default stationNetworkApi;
